import React from 'react'
import styled from 'styled-components'
import { useKey } from 'react-use'

import {
  IconButton,
  Checkbox,
  useForTablet,
  Dropdown,
  DropdownItem,
  SelectorButton,
} from '@sketch/components'

import { ReactComponent as PlusIcon } from '@sketch/icons/circle.plus-svg'
import { ReactComponent as MinusIcon } from '@sketch/icons/circle.minus-svg'
import ChevronsIcon from '@sketch/icons/chevrons-up-down-24.svg'
import { noop, clamp, keyWithoutModifier } from '@sketch/utils'

import { MIN_ZOOM, MAX_ZOOM } from './constants'
import { useArtboardContext } from 'modules/shares/DetailView/ArtboardContext'
import { ZoomTypes } from './types'

const Container = styled.div`
  display: flex;
  flex-direction: row;
  justify-content: space-between;
  align-items: center;
  margin: 0 16px;
`

const IconContainer = styled(IconButton)`
  flex-shrink: 0;
  color: ${({ theme }) => theme.colors.foreground.secondary.C};

  &:hover {
    color: ${({ theme }) => theme.colors.foreground.secondary.A};
  }

  &:disabled {
    opacity: 0.5;
  }
`

const MinusButton = styled(IconContainer)`
  margin-right: 8px;
`

const PlusButton = styled(IconContainer)`
  margin-left: 8px;
`

interface ZoomDropdownItemProps {
  active?: boolean
}

const ZoomDropdownItem = styled(DropdownItem)<ZoomDropdownItemProps>`
  min-width: 6rem;
  color: ${({ theme, active }) => active && theme.colors.sketch.A};
`

const StyledSelectorButton = styled(SelectorButton)`
  font-variant-numeric: tabular-nums;
  width: 82px;
  padding-right: 28px;

  ::after {
    content: '';
    margin: auto;

    background-repeat: no-repeat;
    background-position: center;
    background-image: url(${ChevronsIcon});
    opacity: 0.55;

    width: 24px;
    height: 24px;

    position: absolute;
    top: 0;
    right: 4px;
    bottom: 0;
  }
`

const nearestPowerOf2 = (value: number): number => {
  return 1 << (31 - Math.clz32(value))
}

const clampFactor = (factor: number, min: number, max: number): number => {
  if (factor <= min) {
    return min
  } else if (factor <= 1) {
    return factor
  }

  return nearestPowerOf2(clamp(1, max, factor))
}

const isMin = (zoom: ZoomTypes, min: number) => {
  return zoom && zoom.factor && zoom.factor <= min ? true : false
}

const isMax = (zoom: ZoomTypes, max: number) => {
  return zoom && zoom.factor && zoom.factor >= max ? true : false
}

export type ZoomProps = {
  zoom: ZoomTypes
  onZoomChanged: (newZoom: ZoomTypes) => void
  minZoom?: number
  maxZoom?: number
  navigator?: Pick<Navigator, 'platform'>
}

const Zoom: React.FC<ZoomProps> = ({
  zoom,
  onZoomChanged,
  navigator = window.navigator,
  minZoom = MIN_ZOOM,
  maxZoom = MAX_ZOOM,
}) => {
  const {
    isInspectorEnabled,
    toggleGrid,
    isGridEnabled,
    toggleLayout,
    isLayoutEnabled,
  } = useArtboardContext()

  const factors = [minZoom, 0.5, 1.0, 2.0, 4.0, maxZoom]
  const zoomOut = () => {
    if (typeof zoom.factor !== 'number' || zoom.factor <= factors[0]) {
      return
    }
    const nextFactorIndex = factors.findIndex(
      element => element >= (zoom.factor || 0)
    )
    changeZoom(factors[nextFactorIndex - 1])
  }
  const zoomIn = () => {
    if (
      typeof zoom.factor !== 'number' ||
      zoom.factor >= factors[factors.length - 1]
    ) {
      return
    }
    const nextFactorIndex = factors.findIndex(
      element => element > (zoom.factor || 0)
    )
    changeZoom(factors[nextFactorIndex])
  }

  const zoomActualSize = () => {
    changeZoom(1)
  }

  const zoomToFit = () => {
    onZoomChanged({ type: 'ZOOM_TO_FIT' })
  }

  const changeZoom = (newFactor: number) => {
    onZoomChanged({
      type: 'ZOOM_FACTOR',
      factor: clampFactor(newFactor, minZoom, maxZoom),
    })
  }

  const formatZoom = (zoom: ZoomTypes) => {
    if (typeof zoom.factor !== 'number') {
      return '-'
    }

    return `${Math.round(zoom.factor * 100)}%`
  }

  const isTabletAndBigger = useForTablet()

  useKey(keyWithoutModifier(['=', '+'], navigator), zoomIn)
  useKey(keyWithoutModifier('-', navigator), zoomOut)
  useKey(keyWithoutModifier('0', navigator), zoomActualSize)
  useKey(keyWithoutModifier('1', navigator), zoomToFit)

  const getGridAndLayoutOptions = () => {
    // do not show if we're in the canvas view or not on the inspector tab
    if (!isInspectorEnabled || window.location.pathname.includes('/canvas')) {
      return null
    }

    return (
      <>
        <Dropdown.Divider />
        <Dropdown.Header>Grid/Layout</Dropdown.Header>
        <ZoomDropdownItem onClick={toggleGrid}>
          <Checkbox checked={isGridEnabled} label="Show Grid" />
        </ZoomDropdownItem>
        <ZoomDropdownItem onClick={toggleLayout}>
          <Checkbox checked={isLayoutEnabled} label="Show Layout" />
        </ZoomDropdownItem>
      </>
    )
  }

  return (
    <Container>
      {isTabletAndBigger && (
        <MinusButton
          icon={MinusIcon}
          size={20}
          onClick={zoomOut}
          disabled={isMin(zoom, minZoom)}
          data-testid="zoom-out"
        />
      )}
      <Dropdown
        toggle={
          <StyledSelectorButton
            variant="strong"
            label={formatZoom(zoom)}
            fullWidth
          />
        }
        onToggle={noop}
        minWidth={'6rem'}
        placement="bottom"
        data-testid="zoom-value"
      >
        <Dropdown.Header>Zoom</Dropdown.Header>
        <ZoomDropdownItem
          className={
            zoom.type === 'ZOOM_FACTOR' && zoom.factor === 0.5 ? 'active' : ''
          }
          onClick={() => changeZoom(0.5)}
        >
          50%
        </ZoomDropdownItem>
        <ZoomDropdownItem
          className={
            zoom.type === 'ZOOM_FACTOR' && zoom.factor === 1 ? 'active' : ''
          }
          onClick={() => changeZoom(1)}
        >
          100%
        </ZoomDropdownItem>
        <ZoomDropdownItem
          className={
            zoom.type === 'ZOOM_FACTOR' && zoom.factor === 2 ? 'active' : ''
          }
          onClick={() => changeZoom(2)}
        >
          200%
        </ZoomDropdownItem>
        <ZoomDropdownItem
          className={zoom.type === 'ZOOM_TO_FIT' ? 'active' : ''}
          onClick={() => onZoomChanged({ type: 'ZOOM_TO_FIT' })}
        >
          Zoom to Fit
        </ZoomDropdownItem>

        {getGridAndLayoutOptions()}
      </Dropdown>
      {isTabletAndBigger && (
        <PlusButton
          icon={PlusIcon}
          size={20}
          onClick={zoomIn}
          disabled={isMax(zoom, maxZoom)}
          data-testid="zoom-in"
        />
      )}
    </Container>
  )
}

export { Zoom as ZoomOld }
